import { createLogic } from 'redux-logic';
import { each, find, first, get, unset } from 'lodash';

export const initialState = {
  loading: false,
  entities: {},
  recentlyCompleted: {},
};

const completionTypes = [
  {
    model: 'ExercisePlanDayV2',
    type: 'workouts',
    idenitifer: 'itemId',
  },
  {
    model: 'MealPlanRecipe',
    type: 'meals',
    idenitifer: 'item.dayString',
  },
];

// Actions

const FETCH = '@@app/completions/FETCH';
const FETCH_SUCCESS = '@@app/completions/FETCH_SUCCESS';
const FETCH_ERROR = '@@app/completions/FETCH_ERROR';

const COMPLETE_SUCCESS = '@@app/completions/COMPLETE_SUCCESS';
const COMPLETE_ERROR = '@@app/completions/COMPLETE_ERROR';
const COMPLETE_CLEAR = '@@app/completions/COMPLETE_CLEAR';
const UNCOMPLETE_SUCCESS = '@@app/completions/UNCOMPLETE_SUCCESS';
const UNCOMPLETE_ERROR = '@@app/completions/UNCOMPLETE_ERROR';

const WORKOUT_COMPLETE = '@@app/completions/WORKOUT_COMPLETE';
const WORKOUT_UNCOMPLETE = '@@app/completions/WORKOUT_UNCOMPLETE';
const MEAL_PLAN_COMPLETE = '@@app/completions/MEAL_PLAN_COMPLETE';
const MEAL_PLAN_UNCOMPLETE = '@@app/completions/MEAL_PLAN_UNCOMPLETE';

// Helpers
function complete(obj, completion) {
  const { itemType, referenceName } = completion;
  const { type, idenitifer } = completionType(itemType);
  const id = get(completion, idenitifer);

  // Reference name is week number in this case
  obj[referenceName] = obj[referenceName] || { items: {}, total: 0 };
  obj[referenceName].items[type] = obj[referenceName].items[type] || {};

  // Only incrememnt total if it hasn't been set already
  if (!obj[referenceName].items[type][id]) {
    obj[referenceName].items[type][id] = true;
    obj[referenceName].total += 1;
  }

  return { ...obj };
}

function uncomplete(obj, completion) {
  const { itemType, referenceName } = completion;
  const { type, idenitifer } = completionType(itemType);
  const id = get(completion, idenitifer);

  unset(obj, [referenceName, 'items', type, id]);

  if (get(obj, [referenceName, 'total']) > 0) {
    obj[referenceName].total -= 1;
  }

  return { ...obj };
}

function completionType(type) {
  return (
    find(completionTypes, { model: type }) || find(completionTypes, { type })
  );
}

// Observables
const completedFetchLogic = createLogic({
  type: FETCH,
  process({ action, httpClient }, dispatch, done) {
    const { memberId, voomeProductId, weekNumber } = action.payload;
    const params = { voomeProductId, reference: weekNumber };

    return httpClient
      .get(`/v3/members/${memberId}/completed`, { params })
      .then(({ data }) => data)
      .then(payload => dispatch({ type: FETCH_SUCCESS, payload, weekNumber }))
      .catch(() => dispatch({ type: FETCH_ERROR, error: true }))
      .then(() => done());
  },
});

const completedFetchSuccessLogic = createLogic({
  type: FETCH_SUCCESS,
  transform({ action }, next) {
    const { payload, weekNumber } = action;
    const completions = {};

    each(payload, completion => complete(completions, completion));

    next({ ...action, payload: { completions } });
  },
});

const workoutCompleteLogic = createLogic({
  type: WORKOUT_COMPLETE,
  processOptions: {
    successType: COMPLETE_SUCCESS,
    failType: COMPLETE_ERROR,
  },
  process({ action, httpClient }) {
    const { payload } = action;
    const { exercisePlanId, dayId, voomeProductId, weekNumber } = payload;

    return httpClient
      .put(
        `/v1/voome/exercise-plans/${exercisePlanId}/days/${dayId}/complete`,
        {
          voomeProductId,
          reference: weekNumber,
        }
      )
      .then(response => {
        return { ...response.data.success, ...payload };
      });
  },
});

const workoutUncompleteLogic = createLogic({
  type: WORKOUT_UNCOMPLETE,
  processOptions: {
    successType: UNCOMPLETE_SUCCESS,
    failType: UNCOMPLETE_ERROR,
  },
  process({ action, httpClient }) {
    const { payload } = action;
    const { exercisePlanId, dayId, voomeProductId, weekNumber } = payload;
    const data = { voomeProductId, reference: weekNumber };

    return httpClient
      .delete(
        `/v1/voome/exercise-plans/${exercisePlanId}/days/${dayId}/uncomplete`,
        { data }
      )
      .then(response => {
        const success = first(response.data.success);
        return { ...success, ...payload };
      });
  },
});

const mealPlanCompleteLogic = createLogic({
  type: MEAL_PLAN_COMPLETE,
  processOptions: {
    successType: COMPLETE_SUCCESS,
    failType: COMPLETE_ERROR,
  },
  process({ action, httpClient }) {
    const { payload } = action;
    const { mealPlanId, day, voomeProductId, weekNumber } = payload;

    return httpClient
      .put(`/v1/voome/meal-plans/${mealPlanId}/complete`, {
        voomeProductId,
        reference: weekNumber,
        day,
      })
      .then(response => {
        const success = first(response.data.success);
        return { ...success, ...payload };
      });
  },
});

const mealPlanUncompleteLogic = createLogic({
  type: MEAL_PLAN_UNCOMPLETE,
  processOptions: {
    successType: UNCOMPLETE_SUCCESS,
    failType: UNCOMPLETE_ERROR,
  },
  process({ action, httpClient }) {
    const { payload } = action;
    const { mealPlanId, day, voomeProductId, weekNumber } = payload;
    const data = { voomeProductId, reference: weekNumber, day };

    return httpClient
      .delete(`/v1/voome/meal-plans/${mealPlanId}/uncomplete`, { data })
      .then(response => {
        const success = first(response.data.success);
        return { ...success, ...payload };
      });
  },
});

const completeSuccessLogic = createLogic({
  type: COMPLETE_SUCCESS,
  transform({ action, getState }, next) {
    const { payload } = action;
    const completions = selectCompletions(getState());

    complete(completions, payload);
    next({ ...action });
  },
});

const uncompleteSuccessLogic = createLogic({
  type: UNCOMPLETE_SUCCESS,
  transform({ action, getState }, next) {
    const { payload } = action;
    const completions = selectCompletions(getState());

    uncomplete(completions, payload);
    next({ ...action });
  },
});

export const completionsLogic = [
  completedFetchLogic,
  completedFetchSuccessLogic,
  mealPlanCompleteLogic,
  mealPlanUncompleteLogic,
  workoutCompleteLogic,
  workoutUncompleteLogic,
  completeSuccessLogic,
  uncompleteSuccessLogic,
];

// Selectors
export const selectCompletions = (state, weekNumber = null) => {
  let completions = get(state, 'completions.entities.completions');

  if (weekNumber) {
    completions = get(completions, weekNumber);
  }

  return completions;
};

export const selectRecentlyCompleted = state => {
  return get(state, 'completions.recentlyCompleted');
};

export const selectWorkoutIsComplete = (state, workout = {}) => {
  return get(
    selectCompletions(state, workout.week),
    ['items', 'workouts', workout.id],
    false
  );
};

// Action Creators
export function completionsFetch(memberId, voomeProductId, weekNumber) {
  return { type: FETCH, payload: { memberId, voomeProductId, weekNumber } };
}

export function workoutComplete(
  exercisePlanId,
  dayId,
  voomeProductId,
  weekNumber,
  title
) {
  return {
    type: WORKOUT_COMPLETE,
    payload: { exercisePlanId, dayId, voomeProductId, weekNumber, title },
  };
}

export function workoutUncomplete(
  exercisePlanId,
  dayId,
  voomeProductId,
  weekNumber
) {
  return {
    type: WORKOUT_UNCOMPLETE,
    payload: { exercisePlanId, dayId, voomeProductId, weekNumber },
  };
}

export function workoutToggleComplete(
  exercisePlanId,
  dayId,
  voomeProductId,
  weekNumber,
  title,
  isComplete
) {
  if (isComplete) {
    return workoutUncomplete(exercisePlanId, dayId, voomeProductId, weekNumber);
  } else {
    return workoutComplete(
      exercisePlanId,
      dayId,
      voomeProductId,
      weekNumber,
      title
    );
  }
}

export function mealPlanComplete(
  mealPlanId,
  day,
  voomeProductId,
  weekNumber,
  title
) {
  return {
    type: MEAL_PLAN_COMPLETE,
    payload: { mealPlanId, day, voomeProductId, weekNumber, title },
  };
}

export function mealPlanUncomplete(
  mealPlanId,
  day,
  voomeProductId,
  weekNumber
) {
  return {
    type: MEAL_PLAN_UNCOMPLETE,
    payload: { mealPlanId, day, voomeProductId, weekNumber },
  };
}

export function mealPlanToggleComplete(
  mealPlanId,
  day,
  voomeProductId,
  weekNumber,
  title,
  isComplete
) {
  if (isComplete) {
    return mealPlanUncomplete(mealPlanId, day, voomeProductId, weekNumber);
  } else {
    return mealPlanComplete(mealPlanId, day, voomeProductId, weekNumber, title);
  }
}

export function clearComplete() {
  return { type: COMPLETE_CLEAR };
}

// Reducer
export default function completionReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_SUCCESS:
      return {
        ...state,
        entities: payload,
      };

    case COMPLETE_SUCCESS:
      return {
        ...state,
        recentlyCompleted: payload,
      };

    case COMPLETE_CLEAR:
      return {
        ...state,
        recentlyCompleted: null,
      };

    default:
      return { ...state };
  }
}



// WEBPACK FOOTER //
// ./src/logic/completions.js